/* eslint-disable @typescript-eslint/no-explicit-any */
import { type ConsentDto } from '@numa-group/apis.guest';
import { useEffect } from 'react';
import ContentLoader from 'react-content-loader';
import { FormattedMessage } from 'react-intl';

import { useBrand } from '@src/contexts/brand-context';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useConsentForm } from '@src/features/user-consent/hooks';

import styles from './general-terms-consent.module.css';

export type GeneralTermsConsentProps = {
  guestConsents?: ConsentDto[];
  isLoading: boolean;
  errorMessage?: string;
};

const GeneralTermsConsentSkeleton = () => (
  <ContentLoader width="100%" height="1em">
    <rect width="100%" height="100%" />
  </ContentLoader>
);

export const GeneralTermsConsent = ({ guestConsents, isLoading }: GeneralTermsConsentProps) => {
  const content = useCmsContent();
  const { brand } = useBrand();
  const { register, unregister } = useConsentForm();

  const hasPreviouslyConsentedToGeneralTerms =
    guestConsents?.some((consent) => consent.type === 'general-terms') ?? false;

  useEffect(() => {
    if (hasPreviouslyConsentedToGeneralTerms) {
      unregister('consents.general-terms');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPreviouslyConsentedToGeneralTerms]);

  if (isLoading) {
    return <GeneralTermsConsentSkeleton />;
  }

  return (
    <div className={styles.wrapper}>
      {!hasPreviouslyConsentedToGeneralTerms && (
        <input
          type="hidden"
          {...register?.('consents.general-terms', {
            value: true,
          })}
        />
      )}
      <p>
        <FormattedMessage
          id="acceptTermsCheckbox"
          defaultMessage={content.guestConsentsGeneralTermsText}
          tagName="span"
          values={{
            'link-to-terms': (chunks) => (
              <a href={brand.tncUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            'link-to-privacy': (chunks) => (
              <a href={brand.privacyUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            cta: <strong>{`"${content.signUp}"`}</strong>,
            'link-to-member-terms': (chunks) => (
              <a href={brand.memberTermsUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
            'link-to-declaration': (chunks) => (
              <a href={brand.declarationOfConsentUrl} target="_blank" rel="noreferrer">
                {chunks}
              </a>
            ),
          }}
        />
      </p>
    </div>
  );
};
